import { useMemo } from 'react'
import { gql, useQuery } from '@apollo/client'
import { getCookie, setCookie } from 'cookies-next'
import { REGION_COOKIE } from '@/constants/cookies'
import { Durations } from '@/constants/durations'
import { CANADA, UNITED_STATES } from '@/constants/regions'
import { GetViewerRegionQuery } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { isDevelopmentEnvironment } from '@/utils/environment-utils'
import { Maybe } from '@/utils/types'

const getViewerRegionQuery = gql`
  query getViewerRegion {
    viewer {
      region
    }
  }
`

interface UseUserRegionResult {
  loading: boolean
  region: string | undefined
}

export function useUserRegion(): UseUserRegionResult {
  const result = useQuery<GetViewerRegionQuery>(getViewerRegionQuery, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  })

  return useMemo(() => {
    const hydraRegion = result?.data?.viewer?.region
    const cookieRegion = getRegionFromCookie()
    const region = overrideOrRegion(hydraRegion || cookieRegion)

    if (hydraRegion) {
      setCookie(REGION_COOKIE, hydraRegion, { maxAge: Durations.ONE_MONTH_IN_SECONDS })
    }

    return { loading: result.loading, region }
  }, [result])
}

export function getUserRegionFromReq(req: { headers: Record<string, string | string[] | undefined> }): string | null {
  const vercelRegion = getItemOrFirstEntry(req.headers['x-vercel-ip-country'])
  return overrideOrRegion(vercelRegion) ?? null
}

export function overrideOrRegion(region: Maybe<string>): string | undefined {
  const mockRegion = process.env.NEXT_PUBLIC_MOCK_REGION?.toUpperCase()
  if (isDevelopmentEnvironment() && mockRegion) return mockRegion
  return region?.toUpperCase()
}

export function isUnitedStates(region: Maybe<string>): boolean {
  if (!region) return false
  const upper = region.toUpperCase()
  return Boolean(upper === UNITED_STATES)
}

export function isCanada(region: Maybe<string>): boolean {
  if (!region) return false
  const upper = region.toUpperCase()
  return Boolean(upper === CANADA)
}

export function isUnitedStatesOrCanada(region: Maybe<string>): boolean {
  if (!region) return false
  const upper = region.toUpperCase()
  return Boolean(upper === UNITED_STATES || upper === CANADA)
}

/**
 * NOTE - Only use this function client side. If used server side, the cookie will always be one request behind.
 * Use getUserRegionFromReq server side.
 * @returns
 */
export function getRegionFromCookie(): string | undefined {
  const region = getCookie(REGION_COOKIE)
  return typeof region === 'string' ? region : undefined
}
